import { OnboardingModel } from './model';
import { action, autorun, IReactionDisposer } from 'mobx';
import { EnvModel } from '../env/model';
import { APIController } from '../api/controller';
import { AuthController } from '../auth/controller';
import { FlashController } from '../flash/controller';
import { StorageModel } from '../storage/model';
import { PrismicController } from '../prismic/controller';
import { BugsnagController } from '../bugsnag/controller';
import { AnalyticsController } from '../analytics/controller';
import { ContentModel } from '../content/model';
import { FormOnboardingFields } from '../../components/form-onboarding';
import { FlashMessageTypeMigrationPrompt, HttpError } from '../../types';
import { MIGRATION_PROMPT_SHOWN, MIGRATION_TOAST_SHOWN, ONBOARDING_STATE, SIGN_UP_ACTION_QUERY } from '../../constants';
import { CouponController } from '../coupon/controller';
import { rdt } from '../../reddit-track';
import { APIResponse } from '../api/types';

const DEFAULT_FORM_ERROR = 'Oops! Something went wrong, please try again.';

export class OnboardingController {
  constructor(
    private model: OnboardingModel,
    private env: EnvModel,
    private api: APIController,
    private auth: AuthController,
    private flash: FlashController,
    private storage: StorageModel,
    private bugsnag: BugsnagController,
    private prismic: PrismicController,
    private analytics: AnalyticsController,
    private content: ContentModel,
    private coupon: CouponController,
  ) {
    this._disposers = [];
    if (!env.isServer) {
      this.init();
    }
  }

  private _disposers: IReactionDisposer[];

  dispose = () => {
    this._disposers.forEach((dispose) => dispose());
  };

  private init = async () => {
    await this.env.ready;

    // Flash a toast notification for returning post-migration visitors
    this._disposers.push(
      autorun(() => {
        const toastShown = window.sessionStorage.getItem(MIGRATION_TOAST_SHOWN) === 'true';
        if (!toastShown && this.model.detailsIsComplete && this.storage.getItem(MIGRATION_PROMPT_SHOWN) === true) {
          this.flash.createMessage(FlashMessageTypeMigrationPrompt, {
            onClickPrompt: () => this.storage.delete(MIGRATION_PROMPT_SHOWN),
          });
          window.sessionStorage.setItem(MIGRATION_TOAST_SHOWN, 'true');
        }
      }),
    );

    this._disposers.push(
      autorun(() => {
        if (this.model.user && this.model.hasFetched) {
          this.model.detailsShow = !this.model.detailsIsComplete;

          if (this.model.detailsShow || this.model.dismissAll) {
            return;
          }

          if (!this.storage.getItem('trackLogin')) {
            this.analytics.sendLogin(this.model.user);
            this.storage.setItem('trackLogin', 1);
          }

          const { onboarding_state } = this.model.user;

          switch (onboarding_state.state) {
            case ONBOARDING_STATE.SPOTIFY_UNLINKED:
              return (this.model.spotifyShow = true);

            case ONBOARDING_STATE.TWITTER_UNLINKED:
              this.model.twitterIsOnboarding = true;
              return (this.model.twitterShow = true);
          }
        } else {
          this.model.detailsShow = false;
        }
      }),
    );

    // show the sign up straight away i.e. from JaackMaate
    if (window.location.search.includes(SIGN_UP_ACTION_QUERY)) {
      this.openSignUpModal();
    }
  };

  @action
  changeBetaEmail = (email: string) => {
    this.model.betaEmail.value = email;
  };

  @action
  openBetaModal = () => {
    this.model.betaShow = true;
  };

  @action
  dismissBetaModal = () => {
    this.model.betaShow = false;
  };

  @action
  resetErrors(fields?: FormOnboardingFields) {
    this.model.detailsError = null;
    if (typeof fields === 'object') {
      Object.values(fields).forEach((field) => {
        field.error = undefined;
      });
    }
  }

  @action
  handleFormErrorResponse(error: APIResponse<never>['error'], fields: FormOnboardingFields) {
    if (error) {
      if (typeof error === 'string') {
        this.model.detailsError = error;
        return;
      }
      if (typeof error === 'object' && error !== null) {
        let matched = false;
        Object.keys(error).forEach((name) => {
          if (name in fields) {
            fields[name].error = error[name];
            matched = true;
          }
        });
        if (matched) {
          this.model.detailsError = 'Oops! Something went wrong, please fix the errors and try again.';
          return;
        }
      }
    }
    this.model.detailsError = DEFAULT_FORM_ERROR;
  }

  @action
  submitDetailsModal = async (fields: FormOnboardingFields) => {
    await this.env.ready;

    const values: any = Object.keys(fields).reduce((vals, key) => ({ ...vals, [key]: fields[key].value }), {});
    this.resetErrors(fields);

    if (!values.email || values.email.includes('@pages.plusgoogle.com')) {
      return (this.model.detailsError = 'Please enter your email address');
    }

    if (!values.country) {
      return (this.model.detailsError = 'Please select a country');
    }

    const SIXTEEN_YEARS_HENCE = 1000 * 60 * 60 * 24 * 365.25;
    const age = new Date(values.age);
    const differenceInYears = (Date.now() - age.getTime()) / SIXTEEN_YEARS_HENCE;

    if (differenceInYears < 16) {
      return (this.model.detailsError = this.content.ageWarning);
    }

    if (!values.accepted_terms) return (this.model.detailsError = 'Please accept the terms and conditions');

    try {
      this.model.detailsSaving = true;
      const response = await this.api.user.setContactDetails(values);
      if ('success' in response && response.success === false) {
        this.handleFormErrorResponse(response.error, fields);
        return;
      }
      await this.auth.getSession();
      await this.coupon.getCoupon(this.model.user.default_channel);
      this.model.detailsShow = false;
      this.analytics.sendSignup(this.model.user);
      this.flash.createFromCMS('new_user', { name: this.model.user.first_name });
      this.analytics.sendMixpanel('Sign up complete');
      rdt('track', 'SignUp');
    } catch (e) {
      try {
        this.model.detailsError = (e as HttpError).body.error;
      } catch (e) {
        this.model.detailsError = DEFAULT_FORM_ERROR;
      }
    } finally {
      this.model.detailsSaving = false;
    }
  };

  @action
  openSignUpModal = async (redirect: string = null) => {
    if (this.model.user) return;
    if (this.model.isPermitted) {
      await this.env.ready;
      this.model.signUpRedirect = redirect;
      this.model.signUpShow = true;
    } else {
      this.openBetaModal();
    }
  };

  @action
  closeSignUpModal = () => {
    this.model.signUpShow = false;
  };

  @action
  dismissSignUpCTA = () => {
    this.storage.setItem('hideSignUpCTA', '1');
  };

  @action
  closeSpotifyModal = () => {
    this.model.spotifyShow = false;
    this.model.dismissAll = true;
  };

  @action
  skipSpotifyModal = async () => {
    this.model.spotifySaving = true;

    try {
      await this.api.user.setOnboardingState(ONBOARDING_STATE.SPOTIFY_UNLINKED);
    } finally {
      this.closeSpotifyModal();
      this.model.spotifySaving = false;
    }
  };

  @action
  setTwitterFollow = (follow: boolean) => {
    this.model.twitterFollow = follow;
  };

  @action
  openTwitterModal = () => {
    this.model.twitterIsOnboarding = false;
    this.model.twitterShow = true;
  };

  @action
  closeTwitterModal = () => {
    this.model.twitterShow = false;
    this.model.dismissAll = true;
  };

  @action
  skipTwitterModal = async () => {
    this.model.twitterSaving = true;

    try {
      await this.api.user.setOnboardingState(ONBOARDING_STATE.TWITTER_UNLINKED);
    } finally {
      this.closeTwitterModal();
      this.model.twitterSaving = false;
    }
  };

  @action
  showTermsConditionsModal = async () => {
    this.model.termsConditionsShow = true;
    this.model.termsConditionsLoading = true;

    const document = 'terms__conditions';

    try {
      const { data } = await this.prismic.getSingle(document);

      // RichText
      this.model.termsConditionsPageBody = data.page_body;
    } catch (e) {
      this.bugsnag.notifyException(e);
    } finally {
      this.model.termsConditionsLoading = false;
    }

    return;
  };

  @action
  closeTermsConditionsModal = () => {
    this.model.termsConditionsShow = false;
  };

  @action
  showPrivacyPolicyModal = async () => {
    this.model.privacyPolicyShow = true;
    this.model.privacyPolicyLoading = true;

    const document = 'privacy_policy';

    try {
      const { data } = await this.prismic.getSingle(document);

      // RichText
      this.model.privacyPolicyPageBody = data.page_body;
    } catch (e) {
      this.bugsnag.notifyException(e);
    } finally {
      this.model.privacyPolicyLoading = false;
    }

    return;
  };

  @action
  closePrivacyPolicyModal = () => {
    this.model.privacyPolicyShow = false;
  };
}
